import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = {
  class: "border text-gray-900 bg-white shadow flex flex-col relative",
  style: {"width":"420px"}
}
const _hoisted_2 = { class: "flex-1 flex flex-col overflow-auto px-4 pt-6 pb-3" }
const _hoisted_3 = { class: "mb-8" }
const _hoisted_4 = { class: "text-md font-medium mb-2" }
const _hoisted_5 = {
  key: 0,
  class: "text-right plannedBg rounded text-gray-600 w-min px-1.5 py-0.5 text-sm whitespace-nowrap"
}
const _hoisted_6 = { class: "mb-10" }
const _hoisted_7 = { class: "pt-4 overflow-x-auto" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "pr-4 bg-gray-50 text-xs font-medium text-gray-500 border-b border-gray-200 text-left" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("th", { class: "p-3" }, null, -1)
const _hoisted_11 = { class: "p-3" }
const _hoisted_12 = { class: "p-3" }
const _hoisted_13 = {
  key: 0,
  class: "p-3"
}
const _hoisted_14 = { class: "p-3" }
const _hoisted_15 = { class: "text-xs" }
const _hoisted_16 = { class: "relative flex" }
const _hoisted_17 = { class: "px-3 py-2 whitespace-nowrap" }
const _hoisted_18 = { class: "px-3 py-2 whitespace-nowrap" }
const _hoisted_19 = { class: "px-3 py-2 whitespace-nowrap" }
const _hoisted_20 = {
  key: 0,
  class: "px-3 py-2 whitespace-nowrap"
}
const _hoisted_21 = { class: "pt-4" }
const _hoisted_22 = { class: "font-medium text-md" }

import { XMarkIcon, ChevronLeftIcon } from "@heroicons/vue/24/outline";
import {
  durationService,
  useProjectDurationSettings,
  useIsWorkingDay,
  useNonWorkingDaysByDaySet,
  processClasses,
  ProcessElement,
} from "@oculai/planner";
import { differenceInMinutes, format } from "date-fns";
import { PropType, computed, ref, watch } from "vue";
import { nextTick } from "vue";
import { useI18n } from "vue-i18n";
import ImagesComparison from "@/components/other/ImagesComparison.vue";
import { useOutagesByRange } from "@/composables/outages";
import { useHasProjectFeature } from "@/composables/project";
import { useStreams } from "@/composables/stream";
import { processElementColor } from "@/constants/processClasses";
import { apiClient } from "@/repositories/clients";
import processDurationService from "@/services/processDurationService";
import { ShortenedProcess } from "@/types/Process";

type AnalyzedGroup = {
  name: ProcessElement;
  activeDays: number;
  totalDays: number;
  productiveDays: number;
  outageDays: number;
  workingHours: number;
};

type BeforeAfterMedia = {
  cameraId: string;
  cameraName: string;
  start: string;
  end: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessesAnalysis',
  props: {
  processes: {
    type: Array as PropType<ShortenedProcess[]>,
    required: true,
  },
  start: {
    type: Date,
    required: true,
  },
  end: {
    type: Date,
    required: true,
  },
},
  emits: ["close"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const props = __props;

const { streams } = useStreams();
const { projectDurationSettings } = useProjectDurationSettings(apiClient);
const nonWorkingDaysByDaySet = useNonWorkingDaysByDaySet(apiClient);
const isWorkingDay = useIsWorkingDay(apiClient);
const { outagesByRange } = useOutagesByRange(props.start, props.end);
const hasWorkingHoursFeature = useHasProjectFeature("working_hours");
const { t } = useI18n();

const durationSettings = computed(() =>
  projectDurationSettings.value
    ? durationService.calculateSettings(
        projectDurationSettings.value.working_hours,
        projectDurationSettings.value.non_working_days,
      )
    : null,
);

const outages = computed(() => {
  if (!nonWorkingDaysByDaySet.value) {
    return outagesByRange.value;
  }

  return processDurationService.useCorrectOutages(
    outagesByRange.value,
    streams.value,
    nonWorkingDaysByDaySet.value,
    isWorkingDay.value,
  );
});

const getProcessClassFromEncodedLabel = processClasses.reduce((acc, processClass) => {
  acc[processClass.encodedLabel] = processClass;

  return acc;
}, {} as Record<string, (typeof processClasses)[number]>);

const startDate = ref("");
const endDate = ref("");
const postAnalyzedData = ref<AnalyzedGroup[]>([]);
const beforeAfterMedia = ref<BeforeAfterMedia[]>([]);
const isAnalysisAccordionOpen = ref(true);
const isMediaAccordionOpen = ref(false);
const analysisAccordionContent = ref<HTMLDivElement | null>(null);
const mediaAccordionContent = ref<HTMLDivElement | null>(null);

const toggleAnalysisAccordion = () => {
  isAnalysisAccordionOpen.value = !isAnalysisAccordionOpen.value;

  if (isAnalysisAccordionOpen.value) {
    const height = analysisAccordionContent.value?.children?.[0]?.scrollHeight;
    analysisAccordionContent.value?.style.setProperty("height", `${height}px`);
  } else {
    analysisAccordionContent.value?.style.setProperty("height", "0px");
  }
};

const toggleMediaAccordion = () => {
  isMediaAccordionOpen.value = !isMediaAccordionOpen.value;

  if (isMediaAccordionOpen.value) {
    const height = mediaAccordionContent.value?.children?.[0]?.scrollHeight;
    mediaAccordionContent.value?.style.setProperty("height", `${height}px`);
  } else {
    mediaAccordionContent.value?.style.setProperty("height", "0px");
  }
};

const setAccordionHeight = () => {
  if (isAnalysisAccordionOpen.value) {
    const height = analysisAccordionContent.value?.children?.[0]?.scrollHeight;
    analysisAccordionContent.value?.style.setProperty("height", `${height}px`);
  }

  if (isMediaAccordionOpen.value) {
    const height = mediaAccordionContent.value?.children?.[0]?.scrollHeight;
    mediaAccordionContent.value?.style.setProperty("height", `${height}px`);
  }
};

const calculateWorkingHours = (processes: ShortenedProcess[]) => {
  let sum = 0;

  processes.forEach((process) => {
    process.work_intervals.forEach((interval) => {
      if (interval.workforce.validated_count) {
        sum +=
          differenceInMinutes(interval.end_time, interval.start_time) *
          interval.workforce.validated_count;
      }
    });
  });

  return Math.round(sum / 60);
};

const calculateProductiveDays = (activeHours: number) => {
  const workingDurations = Object.values(durationSettings.value?.workingDaysAndHoursMap || {});
  const totalWorkDays = workingDurations.reduce((acc, duration) => acc + duration.workingDays, 0);
  const totalWorkHours = workingDurations.reduce((acc, duration) => acc + duration.workingHours, 0);
  const avgHoursPerDay = totalWorkHours / totalWorkDays;

  const productiveDays = Math.round((activeHours / avgHoursPerDay) * 10) / 10;

  return productiveDays;
};

const calculateProcessesAnalysis = (processes: ShortenedProcess[]) => {
  nextTick(setAccordionHeight);

  const durationSettingsInstance = durationSettings.value;

  if (!processes.length || !durationSettingsInstance) {
    return;
  }

  const sortedProcesses = processes.slice().sort((a, b) => {
    if (a.start_time.getTime() === b.start_time.getTime()) {
      return a.end_time.getTime() - b.end_time.getTime();
    } else {
      return a.start_time.getTime() - b.start_time.getTime();
    }
  });

  const start = sortedProcesses[0].start_time;
  const end = sortedProcesses.at(-1)?.end_time as Date;

  const processesByGroups = processes.reduce((acc, process) => {
    const processClass = getProcessClassFromEncodedLabel[process.encoded_label];
    const processElement = processClass.processElementLabel;

    if (!acc[processElement]) {
      acc[processElement] = [];
    }

    acc[processElement].push(process);

    return acc;
  }, {} as Record<string, ShortenedProcess[]>);

  if (Object.keys(processesByGroups).length > 1) {
    processesByGroups["total"] = sortedProcesses.slice();
  }

  postAnalyzedData.value = Object.entries(processesByGroups).map(([name, processesGroup]) => {
    const sortedProcessGroup = processesGroup.slice().sort((a, b) => {
      if (a.start_time.getTime() === b.start_time.getTime()) {
        return a.end_time.getTime() - b.end_time.getTime();
      } else {
        return a.start_time.getTime() - b.start_time.getTime();
      }
    });

    const workingHoursDuration = hasWorkingHoursFeature
      ? calculateWorkingHours(sortedProcessGroup)
      : 0;

    const processDetailMap = projectDurationSettings.value
      ? processDurationService.getProcessDetailMap(
          sortedProcessGroup,
          projectDurationSettings.value,
          outages.value,
        )
      : {};

    const processDetailSummary = processDurationService.getProcessDetailSummary(processDetailMap);
    const totalDuration = processDurationService.getTotalDuration(processDetailSummary);
    const activeHours =
      processDurationService.millisecondsToHours(
        processDurationService.calculateDurationSumExcludingOverlaps(sortedProcessGroup),
      ) || 1;
    const productiveDays = calculateProductiveDays(activeHours);

    return {
      name: name as ProcessElement,
      activeDays: processDetailSummary.active,
      totalDays: totalDuration,
      outageDays: processDetailSummary.outage,
      productiveDays: productiveDays,
      workingHours: workingHoursDuration,
    };
  });

  startDate.value = format(start, "dd.MM.yyyy");
  endDate.value = format(end, "dd.MM.yyyy");

  const firstAndLastProcessHaveSameCamera = processes[0].camera_id === processes.at(-1)?.camera_id;

  const firstCameraId = processes[0].camera_id;
  const firstCameraName = streams.value.find((stream) => stream.camera_id === firstCameraId)?.name;
  if (firstAndLastProcessHaveSameCamera) {
    beforeAfterMedia.value = [
      {
        cameraId: firstCameraId,
        cameraName: firstCameraName || "",
        start: format(start, "yyyy-MM-dd"),
        end: format(end, "yyyy-MM-dd"),
      },
    ];
  } else {
    const lastCameraId = processes.at(-1)?.camera_id;
    const lastCameraName = streams.value.find((stream) => stream.camera_id === lastCameraId)?.name;
    const lastProcessWithFirstCamera = processes
      .slice()
      .reverse()
      .find((process) => process.camera_id === firstCameraId);
    const firstProcessWithLastCamera = processes.find(
      (process) => process.camera_id === lastCameraId,
    );

    beforeAfterMedia.value = [
      {
        cameraId: firstCameraId,
        cameraName: firstCameraName || "",
        start: format(start, "yyyy-MM-dd"),
        end: format(lastProcessWithFirstCamera?.end_time || start, "yyyy-MM-dd"),
      },
      {
        cameraId: lastCameraId as string,
        cameraName: lastCameraName || "",
        start: format(firstProcessWithLastCamera?.start_time || end, "yyyy-MM-dd"),
        end: format(end, "yyyy-MM-dd"),
      },
    ];
  }
};

watch(
  () => props.processes,
  (processes) => calculateProcessesAnalysis(processes),
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, _toDisplayString(__props.processes.length) + " " + _toDisplayString(_unref(t)("analytics.processes.sidebar.processes_selected", __props.processes.length)), 1),
        (__props.processes.length)
          ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(startDate.value) + " - " + _toDisplayString(endDate.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", {
          class: "border-b pb-5 flex justify-between cursor-pointer",
          onClick: toggleAnalysisAccordion
        }, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)("analytics.processes.sidebar.analysis")), 1),
          _createVNode(_unref(ChevronLeftIcon), {
            class: _normalizeClass([
              'h-5 w-5 transition-[transform] duration-200',
              isAnalysisAccordionOpen.value ? 'transform -rotate-90' : '',
            ])
          }, null, 8, ["class"])
        ]),
        _createElementVNode("div", {
          ref_key: "analysisAccordionContent",
          ref: analysisAccordionContent,
          class: "overflow-hidden transition-[height] duration-200"
        }, [
          _createElementVNode("div", _hoisted_7, [
            (__props.processes.length)
              ? (_openBlock(), _createElementBlock("table", _hoisted_8, [
                  _createElementVNode("thead", null, [
                    _createElementVNode("tr", _hoisted_9, [
                      _hoisted_10,
                      _createElementVNode("th", _hoisted_11, _toDisplayString(_unref(t)("analytics.processes.sidebar.active_total_days")), 1),
                      _createElementVNode("th", _hoisted_12, _toDisplayString(_unref(t)("analytics.processes.sidebar.productive_days")), 1),
                      (postAnalyzedData.value.at(-1)?.outageDays)
                        ? (_openBlock(), _createElementBlock("th", _hoisted_13, _toDisplayString(_unref(t)("analytics.processes.sidebar.outage_days")), 1))
                        : _createCommentVNode("", true),
                      _createElementVNode("th", _hoisted_14, _toDisplayString(_unref(t)("analytics.processes.sidebar.working_hours")), 1)
                    ])
                  ]),
                  _createElementVNode("tbody", _hoisted_15, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(postAnalyzedData.value, (group) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: group.name,
                        class: "border-b border-gray-200"
                      }, [
                        _createElementVNode("td", _hoisted_16, [
                          _createElementVNode("div", {
                            class: "w-1",
                            style: _normalizeStyle(`background: ${_unref(processElementColor)[group.name]}`)
                          }, null, 4),
                          _createElementVNode("div", _hoisted_17, _toDisplayString(group.name === ("total" as string)
                          ? _unref(t)("analytics.processes.sidebar.total")
                          : _unref(t)(`report.sections.processes.process_type_entries.${group.name}`)), 1)
                        ]),
                        _createElementVNode("td", _hoisted_18, _toDisplayString(group.activeDays) + "/" + _toDisplayString(group.totalDays) + " " + _toDisplayString(_unref(t)("time.day", 2)), 1),
                        _createElementVNode("td", _hoisted_19, _toDisplayString(group.productiveDays) + " " + _toDisplayString(_unref(t)("time.day", group.productiveDays)), 1),
                        (postAnalyzedData.value.at(-1)?.outageDays)
                          ? (_openBlock(), _createElementBlock("td", _hoisted_20, _toDisplayString((group.name as string) === "total"
                        ? ""
                        : `${group.outageDays} ${_unref(t)("time.day", group.outageDays)}`), 1))
                          : _createCommentVNode("", true),
                        _createElementVNode("td", {
                          class: _normalizeClass(['px-3 py-2 whitespace-nowrap', !_unref(hasWorkingHoursFeature) && 'blur-sm'])
                        }, _toDisplayString(group.workingHours) + " " + _toDisplayString(_unref(t)("time.hour", group.workingHours)), 3)
                      ]))
                    }), 128))
                  ])
                ]))
              : _createCommentVNode("", true)
          ])
        ], 512)
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("div", {
          class: "border-b pb-5 flex justify-between cursor-pointer",
          onClick: toggleMediaAccordion
        }, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)("analytics.processes.sidebar.before_after")), 1),
          _createVNode(_unref(ChevronLeftIcon), {
            class: _normalizeClass([
              'h-5 w-5 transition-[transform] duration-200',
              isMediaAccordionOpen.value ? 'transform -rotate-90' : '',
            ])
          }, null, 8, ["class"])
        ]),
        _createElementVNode("div", {
          ref_key: "mediaAccordionContent",
          ref: mediaAccordionContent,
          class: "overflow-hidden transition-[height] duration-200 h-0"
        }, [
          _createElementVNode("div", _hoisted_21, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(beforeAfterMedia.value, (media) => {
              return (_openBlock(), _createElementBlock("div", {
                key: media.cameraId,
                class: "mb-4"
              }, [
                _createElementVNode("p", _hoisted_22, _toDisplayString(media.cameraName), 1),
                _createVNode(ImagesComparison, {
                  initialBeforeDate: media.start,
                  initialAfterDate: media.end,
                  camera: media.cameraId
                }, null, 8, ["initialBeforeDate", "initialAfterDate", "camera"])
              ]))
            }), 128))
          ])
        ], 512)
      ])
    ]),
    _createVNode(_unref(XMarkIcon), {
      class: "h-5 w-5 absolute cursor-pointer top-3 right-4 text-gray-600",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
    })
  ]))
}
}

})